import { useState } from "react";
import { Scale, CheckCircle } from "lucide-react";

export default function ContactOverlay({ onConfirm }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (form.phone.replace(/\D/g, "").length < 10) e.phone = "Valid phone number required";
    if (!form.email.trim() || !form.email.includes("@")) e.email = "Valid email required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    onConfirm({ name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-5"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}>

      <div className="w-full max-w-sm rounded-2xl border border-border/60 bg-background shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center gap-3 border-b border-border/30">
          <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0">
            <Scale className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">Before we get started</p>
            <p className="text-xs text-muted-foreground mt-0.5">Quick intro so we know who to reach</p>
          </div>
        </div>

        {/* Fields */}
        <div className="px-6 py-5 space-y-4">
          <Field label="Full Name" error={errors.name}>
            <input
              type="text"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              placeholder="Jane Smith"
              autoComplete="name"
              className="w-full h-11 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
            />
          </Field>
          <Field label="Phone Number" error={errors.phone}>
            <input
              type="tel"
              value={form.phone}
              onChange={e => set("phone", e.target.value)}
              placeholder="(555) 555-5555"
              autoComplete="tel"
              className="w-full h-11 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
            />
          </Field>
          <Field label="Email Address" error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={e => set("email", e.target.value)}
              placeholder="jane@example.com"
              autoComplete="email"
              className="w-full h-11 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
            />
          </Field>
        </div>

        {/* Confirm button */}
        <div className="px-6 pb-6">
          <button
            onClick={handleConfirm}
            className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold flex items-center justify-center gap-2 transition-all shadow-[0_0_20px_-5px_rgba(99,102,241,0.5)] active:scale-[0.98]"
          >
            <CheckCircle className="w-4 h-4" />
            Let's go
          </button>
          <p className="text-[10px] text-muted-foreground/40 text-center mt-3">
            Your information is encrypted and only shared with matched attorneys.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}
